import axios from 'axios';

const TOKEN_KEY = process.env.REACT_APP_TOKEN_STORAGE_KEY || 'auth_token';

const REFRESH_KEY = process.env.REACT_APP_REFRESH_TOKEN_KEY || 'refresh_token';

function storeTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEY, accessToken);

  localStorage.setItem(REFRESH_KEY, refreshToken);
}

function clearStoredTokens(): void {
  localStorage.removeItem(TOKEN_KEY);

  localStorage.removeItem(REFRESH_KEY);
}

async function requestTokenRefresh(refreshToken: string): Promise<{ access_token: string; refresh_token?: string }> {
  // A bare client prevents refresh requests from re-entering auth interceptors.
  const response = await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/v1/auth/refresh-token`, {
    refresh_token: refreshToken,
  }, { timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '10000') });

  return response.data;
}

export const tokenService = {
  setTokens(accessToken: string, refreshToken: string) {
    storeTokens(accessToken, refreshToken);
  },

  getAccessToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_KEY);
  },

  clearTokens() {
    clearStoredTokens();
    // Remove the early-render hint class so auth buttons become visible again
    document.documentElement.classList.remove('has-token');
  },

  hasToken(): boolean {
    return !!this.getAccessToken();
  },

  _refreshPromise: null as Promise<string> | null,

  async refreshToken(): Promise<string> {
    // Singleton lock: if a refresh is already in-flight, reuse it
    if (this._refreshPromise) return this._refreshPromise;

    this._refreshPromise = (async () => {
      const rt = this.getRefreshToken();

      if (!rt) throw new Error('No refresh token available');

      const { access_token, refresh_token: newRt } = await requestTokenRefresh(rt);

      this.setTokens(access_token, newRt || rt);

      return access_token;
    })().finally(() => { this._refreshPromise = null; });

    return this._refreshPromise;
  },
};
